import { PropsWithChildren, useCallback } from "react"
import { Grid, Stack } from "@mui/material"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { ErrorBoundaryModal } from "../../errors/ErrorBoundaryModal"
import { StandardDialog } from "../../dialogs/StandardDialog"
import { StandardButton } from "../../buttons/StandardButton"
import {
    QueryErrorBoundary,
    QueryErrorFallback,
    QueryErrorFallbackProps,
} from "../../../../core/api/components/QueryErrorBoundary"
import { CreateOrderDialogFlow } from "../createOrderTypes"
import { SafeArea } from "../../skeleton/SafeArea"

type ConfigErrorFallbackProps = QueryErrorFallbackProps &
    Readonly<{
        onClose: () => void
        flowConfig?: CreateOrderDialogFlow
    }>

function ConfigErrorFallback({ onClose, flowConfig, ...rest }: ConfigErrorFallbackProps) {
    return (
        <StandardDialog
            open
            onClose={onClose}
            maxWidthProp="sm"
            dialogTitle={
                <LocalizedStrict id="app-error-boundary-title">
                    <>Something went wrong</>
                </LocalizedStrict>
            }
        >
            <Grid container justifyContent="center" alignItems="center" direction="column" wrap="nowrap">
                <QueryErrorFallback showError {...rest} />
                <Stack alignItems="center" padding="2rem" paddingTop={0}>
                    <LocalizedStrict id="app-error-boundary-button">
                        <StandardButton fullWidth={false} onClick={onClose}>
                            Close dialog
                        </StandardButton>
                    </LocalizedStrict>
                </Stack>
            </Grid>
        </StandardDialog>
    )
}

export type ConfigErrorBoundaryProps = PropsWithChildren<{
    onClose: () => void
    flowConfig?: CreateOrderDialogFlow
}>
export function ConfigErrorBoundary({ children, onClose, flowConfig }: ConfigErrorBoundaryProps) {
    const Fallback = useCallback(
        (props: QueryErrorFallbackProps) => (
            <ConfigErrorFallback onClose={onClose} flowConfig={flowConfig} {...props} />
        ),
        [flowConfig], // Removed onClose as deps - it causes double closing and blinking
    )

    return (
        <ErrorBoundaryModal onClose={onClose}>
            <SafeArea>
                <QueryErrorBoundary Fallback={Fallback}>{children}</QueryErrorBoundary>
            </SafeArea>
        </ErrorBoundaryModal>
    )
}
